import { HTTPError } from "ky";

/** Extract the server's `detail` string from a ky HTTPError response body.
 *
 * ky's own `err.message` is the useless "Request failed with status code
 * 403" — the actionable copy (quota explanations, guard messages) is in the
 * FastAPI `{detail}` body and was never surfaced (2026-07-11 quota-403
 * incident). Falls back to `err.message`, then `fallback`.
 */
export async function apiErrorDetail(
  err: unknown,
  fallback: string,
): Promise<string> {
  if (err instanceof HTTPError) {
    try {
      // clone(): the body may already be consumed by other handlers.
      const body = await err.response.clone().json();
      const detail = (body as { detail?: unknown })?.detail;
      if (typeof detail === "string" && detail) return detail;
    } catch {
      // non-JSON body — fall through
    }
    return err.message || fallback;
  }
  return err instanceof Error && err.message ? err.message : fallback;
}

/** True for a plan-cap 403 (`X-Error-Code: upgrade_required`). The detail
 * fallback covers a backend without CORS `expose_headers` (N-1). */
export async function isUpgradeRequired(err: unknown): Promise<boolean> {
  if (!(err instanceof HTTPError) || err.response.status !== 403) return false;
  if (err.response.headers.get("X-Error-Code") === "upgrade_required") return true;
  return (await apiErrorDetail(err, "")).includes("Upgrade for more angles");
}
